import React, { useState, useEffect } from "react";
import { X, MapPin, Building, Globe, AlertCircle } from "lucide-react";
import { Site } from "../../types";
import AddressAutocomplete, {
  AddressDetails,
} from "../GoogleMap/AddressAutocomplete.tsx";

interface SiteFormData {
  name: string;
  siteType: "region" | "building" | "location";
  parentId?: string;
  detail: {
    address?: string;
    description?: string;
    contact?: string;
    phone?: string;
  };
}

interface SiteModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: SiteFormData) => Promise<void>;
  site?: Site | null;
  loading?: boolean;
  parentSites?: Site[];
}

const AddSiteModal: React.FC<SiteModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  site,
  loading = false,
  parentSites = [],
}) => {
  const isEditMode = !!site;
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState<string>("");

  const [form, setForm] = useState<SiteFormData>({
    name: "",
    siteType: "building",
    parentId: "",
    detail: {
      address: "",
      description: "",
      contact: "",
      phone: "",
    },
  });

  useEffect(() => {
    if (site) {
      setForm({
        name: site.name,
        siteType:
          site.type === "region_type"
            ? "region"
            : site.type === "node_type"
            ? "building"
            : site.type === "location_type"
            ? "location"
            : site.type,
        parentId: site.parentId || "",
        detail: {
          address: site.detail?.address || "",
          description: site.detail?.description || "",
          contact: site.detail?.contact || "",
          phone: site.detail?.phone || "",
        },
      });
    } else {
      setForm({
        name: "",
        siteType: "building",
        parentId: "",
        detail: {
          address: "",
          description: "",
          contact: "",
          phone: "",
        },
      });
    }
    setErrors({});
    setSubmitError("");
  }, [site, isOpen]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!form.name.trim()) {
      newErrors.name = "Site name is required";
    } else if (form.name.trim().length < 2) {
      newErrors.name = "Site name must be at least 2 characters";
    }

    if (!form.siteType) {
      newErrors.siteType = "Site type is required";
    }

    if (form.detail.phone && !/^[\d\s\-+()]+$/.test(form.detail.phone)) {
      newErrors.phone = "Please enter a valid phone number";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError("");

    if (!validateForm()) {
      return;
    }

    try {
      // Debug: Log the form state before submitting
      console.log("Form state before submit:", form);
      await onSubmit(form);
    } catch (error) {
      setSubmitError(
        error instanceof Error ? error.message : "An error occurred"
      );
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  const getSiteTypeIcon = (type: string) => {
    switch (type) {
      case "region":
        return <Globe className="w-4 h-4 text-blue-600" />;
      case "building":
        return <Building className="w-4 h-4 text-green-600" />;
      case "location":
        return <MapPin className="w-4 h-4 text-orange-600" />;
      default:
        return <Building className="w-4 h-4 text-gray-600" />;
    }
  };

  const getSiteTypeDescription = (type: string) => {
    switch (type) {
      case "region":
        return "A large geographical area containing multiple buildings";
      case "building":
        return "A physical building or structure";
      case "location":
        return "A specific location within a building";
      default:
        return "";
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center space-x-3">
            {isEditMode && getSiteTypeIcon(form.siteType)}
            <h2 className="text-xl font-semibold text-gray-900">
              {isEditMode ? "Edit Site" : "Create New Site"}
            </h2>
          </div>
          <button
            onClick={handleClose}
            disabled={loading}
            className="text-gray-400 hover:text-gray-600 disabled:opacity-50 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto flex-1 min-h-0">
          {submitError && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center space-x-2">
              <AlertCircle className="w-4 h-4 text-red-600" />
              <span className="text-sm text-red-700">{submitError}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900 border-b border-gray-200 pb-2">
                Basic Information
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Site Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    disabled={loading}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 transition-colors ${
                      errors.name
                        ? "border-red-300 focus:ring-red-500"
                        : "border-gray-300"
                    }`}
                    placeholder="Enter site name"
                  />
                  {errors.name && (
                    <p className="mt-1 text-sm text-red-600">{errors.name}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Site Type *
                  </label>
                  <select
                    required
                    value={form.siteType}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        siteType: e.target.value as
                          | "region"
                          | "building"
                          | "location",
                      })
                    }
                    disabled={loading}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 transition-colors ${
                      errors.siteType
                        ? "border-red-300 focus:ring-red-500"
                        : "border-gray-300"
                    }`}
                  >
                    <option value="region">Region</option>
                    <option value="building">Building</option>
                    <option value="location">Location</option>
                  </select>
                  {errors.siteType && (
                    <p className="mt-1 text-sm text-red-600">
                      {errors.siteType}
                    </p>
                  )}
                  <p className="mt-1 text-xs text-gray-500">
                    {getSiteTypeDescription(form.siteType)}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Parent Site
                  </label>
                  <select
                    value={form.parentId}
                    onChange={(e) =>
                      setForm({ ...form, parentId: e.target.value })
                    }
                    disabled={loading}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 transition-colors"
                  >
                    <option value="">No Parent Site</option>
                    {parentSites
                      .filter((parentSite) =>
                        isEditMode ? parentSite.id !== site?.id : true
                      )
                      .map((site) => (
                        <option key={site.id} value={site.id}>
                          {site.name} ({site.type})
                        </option>
                      ))}
                  </select>
                  <p className="mt-1 text-xs text-gray-500">
                    {isEditMode
                      ? "Change the parent site for this site"
                      : "Optional: Select a parent site for hierarchical organization"}
                  </p>
                </div>
              </div>
            </div>

            {/* Site Details */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900 border-b border-gray-200 pb-2">
                Site Details
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Address
                  </label>
                  <AddressAutocomplete
                    value={form.detail.address || ""}
                    onChange={(address: string, details?: AddressDetails) => {
                      setForm({
                        ...form,
                        detail: { ...form.detail, address },
                      });
                    }}
                    onSelect={(details: AddressDetails) => {
                      console.log("Selected address details:", details);
                      // You can store lat/lng here if needed in the future
                      // For now, just store the formatted address
                      setForm({
                        ...form,
                        detail: {
                          ...form.detail,
                          address: details.formattedAddress,
                        },
                      });
                    }}
                    disabled={loading}
                    apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}
                    placeholder="Start typing an address..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Contact Person
                  </label>
                  <input
                    type="text"
                    value={form.detail.contact}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        detail: { ...form.detail, contact: e.target.value },
                      })
                    }
                    disabled={loading}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 transition-colors"
                    placeholder="Contact person name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    value={form.detail.phone}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        detail: { ...form.detail, phone: e.target.value },
                      })
                    }
                    disabled={loading}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 transition-colors ${
                      errors.phone
                        ? "border-red-300 focus:ring-red-500"
                        : "border-gray-300"
                    }`}
                    placeholder="Contact phone number"
                  />
                  {errors.phone && (
                    <p className="mt-1 text-sm text-red-600">{errors.phone}</p>
                  )}
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    value={form.detail.description}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        detail: { ...form.detail, description: e.target.value },
                      })
                    }
                    disabled={loading}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 transition-colors resize-none"
                    placeholder="Additional notes or description about this site"
                  />
                </div>
              </div>
            </div>
          </form>
        </div>

        {/* Footer */}
        <div className="flex justify-end space-x-3 p-6 border-t border-gray-200 bg-gray-50 flex-shrink-0">
          <button
            type="button"
            onClick={handleClose}
            disabled={loading}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            onClick={handleSubmit}
            disabled={loading}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center space-x-2"
          >
            {loading && (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
            )}
            <span>
              {loading
                ? isEditMode
                  ? "Updating..."
                  : "Creating..."
                : isEditMode
                ? "Update Site"
                : "Create Site"}
            </span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddSiteModal;
